import { request } from '@/api/core';
import * as BusinessTypes from '@/@types/businessTypes';
import * as ImportSessionTypes from '@/@types/importSessionTypes';

/**
 * @typedef {Object} RhapsodyBusinessesPaginatedResponse
 * @property {BusinessTypes.RhapsodyBusiness[]} items
 * @property {number} totalCount
 */

/**
 * Fetches Rhapsody businesses based on the search params
 * @param {Object} FetchBusinessesRequestParams
 * @param {string} FetchBusinessesRequestParams.search
 * @param {number=} FetchBusinessesRequestParams.from
 * @param {number=} FetchBusinessesRequestParams.to
 * @param {string} FetchBusinessesRequestParams.environment
 * @returns {Promise<RhapsodyBusinessesPaginatedResponse>}
 */
export const fetchBusinesses = ({ search, from = 0, to = 20, environment }) => {
  return request('businesses', {
    method: 'GET',
    params: {
      search,
      from,
      to,
      environment
    }
  });
};

/**
 * Returns all V2 Import Sessions associated with the business id
 * @param {string} businessId
 * @param {string} environment
 * @returns {Promise<ImportSessionTypes.ImportSessionV2[]>}
 */
export const fetchBusinessSessions = (businessId, environment) => {
  return request('businesses/sessions', {
    method: 'GET',
    params: {
      businessId,
      environment
    }
  });
};

/**
 * Fetches the source and destination data locations for a given session
 * @param {string} sessionId
 * @param {string} environment
 * @returns {Promise<{ sourceDataLocation: string=; destinationDataLocation: string=}>}
 */
export const fetchBusinessSessionSourceDataLocation = (
  sessionId,
  environment
) => {
  return request('businesses/session-source-data-location', {
    method: 'GET',
    params: {
      sessionId,
      environment
    }
  });
};

/**
 * Fetches a list of the supported session static files and flags to indicate whether they exist
 * @param {string} sessionId
 * @param {string} environment
 * @returns {Promise<[{name: string, extension: string, exists: boolean}]>} List of supported static files
 */
export const fetchBusinessSessionFiles = (sessionId, environment) => {
  return request('businesses/session-files', {
    method: 'GET',
    params: {
      sessionId,
      environment
    }
  });
};

/**
 * Returns the list of sites available for a group clinic. Used when working with grouped clinics that
 * store multiple practices within one dataset and we need to distingish a single practice's data
 * @param {string} environment
 * @param {string} sessionId
 * @returns {Promise<[{site: string, name: string, available: boolean }]>}
 */
export const fetchSessionSites = (environment, sessionId) => {
  return request('businesses/sessions/sites', {
    method: 'GET',
    params: {
      environment,
      sessionId
    }
  });
};

/**
 * Fetches the settings for a given session id
 * @param {string} environment
 * @param {string} sessionId
 * @returns {Promise<ImportSessionTypes.ImportSessionSettingsV2>}
 */
export const fetchSessionSettings = (environment, sessionId) => {
  return request('businesses/sessions/settings', {
    method: 'GET',
    params: {
      environment,
      sessionId
    }
  });
};

/**
 * Used to update the site for a given group import session
 * @param {string} environment
 * @param {string} sessionId
 * @param {string} site
 * @returns {Promise<string>} A string indicating the site has been updated
 */
export const updateSessionSite = (environment, sessionId, site) => {
  return request('businesses/sessions/sites', {
    method: 'POST',
    data: {
      environment,
      sessionId,
      site
    }
  });
};

/**
 * Updates the specified settings for the session
 * @param {string} environment
 * @param {string} sessionId
 * @param {Partial<ImportSessionTypes.ImportSessionSettingsV2>} settings
 * @returns {Promise<ImportSessionTypes.ImportSessionSettingsV2} The updated session settings
 */
export const updateSessionSettings = (environment, sessionId, settings) => {
  return request('businesses/sessions/settings', {
    method: 'PUT',
    params: {
      environment,
      sessionId
    },
    data: {
      settings
    }
  });
};

/**
 * Fetches the businesses saved to the Matcher DB. Typically used for searching Matcher tasks by business
 * @returns {Promise<BusinessTypes.SavedBusiness>}
 */
export const fetchSavedBusinesses = () => request('businesses/saved');
